<template>
  <div class="solar-plant-wallboxes">
    <div v-if="!wallboxes || wallboxes.length === 0" class="text-muted text-center py-3">
      <i class="fa fa-car fa-2x mb-2 d-block"></i>
      {{ $t('no_wallboxes_added') }}
    </div>
    <div v-else>
      <b-row
        v-for="(wallbox, index) in wallboxes"
        :key="getWallboxKey(wallbox, index)"
        class="wallbox-row mb-2"
      >
        <b-col lg="10" md="6" sm="12">
          <div class="d-flex align-items-center">
            <div class="wallbox-info flex-grow-1">
              <router-link
                v-if="wallbox.item && wallbox.item.number"
                target="_blank"
                :to="{
                  name: 'ItemEditPage',
                  params: { itemNumber: wallbox.item.number },
                }"
                class="mr-2"
              >
                #{{ wallbox.item.number.toString().padStart(4, '0') }}
                <i class="fa fa-sm fa-external-link"></i>
              </router-link>
              <span v-if="getManufacturer(wallbox.item)">
                {{ getManufacturer(wallbox.item) }} - {{ getType(wallbox.item) }}
              </span>
              <span v-else class="text-muted">-</span>
              <span v-if="getPower(wallbox.item)" class="ml-2 text-muted">
                ({{ getPower(wallbox.item) }} kW)
              </span>
            </div>
            <div class="quantity-controls d-flex align-items-center ml-3">
              <button
                class="btn btn-sm btn-danger"
                @click="decreaseCount(index)"
                :disabled="wallbox.count <= 1"
              >
                <i class="fa fa-minus"></i>
              </button>
              <input
                v-model.number="wallbox.count"
                type="number"
                min="1"
                class="form-control form-control-sm mx-1 quantity-input"
                @input="updateCount(index, $event.target.value)"
              />
              <button class="btn btn-sm btn-success" @click="increaseCount(index)">
                <i class="fa fa-plus"></i>
              </button>
            </div>
            <button
              class="btn btn-sm btn-danger ml-2"
              @click="removeWallbox(index)"
              :title="$t('remove')"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1">
            <span v-if="formatWallboxTotalPower(wallbox)"
              >{{ formatWallboxTotalPower(wallbox) }} kW</span
            >
            <span v-else class="text-muted">-</span>
          </div>
        </b-col>
      </b-row>

      <!-- Total Power Row -->
      <b-row class="mt-3">
        <b-col lg="10" md="6" sm="12">
          <div class="text-right py-1">
            <strong>{{ $t('wallbox') }} {{ $t('totalPower') }}</strong>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1 bg-light">
            <strong>{{ totalPower }} kW</strong>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  formatSolarPlantTotalWallboxPower,
  formatWallboxesTotalPower,
  wallboxPower,
} from '@/helpers/solarPlant';

export default {
  name: 'SolarPlantWallboxes',
  props: {
    wallboxes: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    totalCount() {
      return this.wallboxes.reduce((total, wallbox) => total + parseInt(wallbox.count || 0, 10), 0);
    },
    totalPower() {
      return formatSolarPlantTotalWallboxPower(this.wallboxes);
    },
  },
  methods: {
    getWallboxKey(wallbox, index) {
      return wallbox.item?._id || wallbox.item || `wallbox-${index}`;
    },
    getManufacturer(item) {
      return item?.wallbox?.manufacturer || '-';
    },
    getType(item) {
      return item?.wallbox?.type || '-';
    },
    getPower: wallboxPower,
    formatWallboxTotalPower: formatWallboxesTotalPower,
    increaseCount(index) {
      const updatedWallboxes = [...this.wallboxes];
      updatedWallboxes[index] = {
        ...updatedWallboxes[index],
        count: parseInt(updatedWallboxes[index].count || 0, 10) + 1,
      };
      this.$emit('update-wallboxes', updatedWallboxes);
    },
    decreaseCount(index) {
      const updatedWallboxes = [...this.wallboxes];
      const currentCount = parseInt(updatedWallboxes[index].count || 0, 10);
      if (currentCount > 1) {
        updatedWallboxes[index] = {
          ...updatedWallboxes[index],
          count: currentCount - 1,
        };
        this.$emit('update-wallboxes', updatedWallboxes);
      }
    },
    updateCount(index, value) {
      const count = parseInt(value, 10);
      if (count >= 1) {
        const updatedWallboxes = [...this.wallboxes];
        updatedWallboxes[index] = {
          ...updatedWallboxes[index],
          count,
        };
        this.$emit('update-wallboxes', updatedWallboxes);
      }
    },
    removeWallbox(index) {
      const updatedWallboxes = this.wallboxes.filter((_, i) => i !== index);
      this.$emit('update-wallboxes', updatedWallboxes);
    },
  },
};
</script>

<style scoped lang="scss">
.quantity-input {
  width: 60px;
  text-align: center;
}

.quantity-controls {
  gap: 4px;
}

.wallbox-row {
  padding: 0.25rem 0;

  &:hover {
    background-color: #f8f9fa;
  }
}

.wallbox-info {
  min-height: 32px;
  display: flex;
  align-items: center;
}
</style>
